import { useMemo, useRef, type MutableRefObject } from 'react';

/**
 * One wells query per distinct payload, per caller, per mount.
 *
 * The Map card, the Explorer and the Explorer's map panel all POST the same payload,
 * and this stops any one of them re-firing it on every render. The dedup is per caller
 * and not global, because they do NOT share a store: the Map card and the map panel each
 * sit inside a `ProvideRootResource`, which is a fresh jotai Provider, while the Explorer
 * reads the root store. A global key let the Map card's fetch satisfy the Explorer's — so
 * the Explorer's store was never written and the well read "location data is not
 * available" on every load. Three stores need three fetches; each just should not fetch
 * the same thing twice.
 *
 * And per mount, not per module: the caller's store is created with the caller, so a key
 * that outlived the caller skipped the fetch on the next visit to the same well while the
 * new store sat empty — the Map card read "No Data Available" on every revisit. The key
 * lives in a ref and dies with the component.
 */

interface WellMapRequest {
  url: string;
  type: 'POST';
  data: Record<string, unknown>;
}

/** The last payload a caller sent, held for the caller's lifetime. */
export type WellMapRequestKey = MutableRefObject<string | null>;

/**
 * Sends the wells query unless this caller's last request was identical.
 * @param {WellMapRequestKey} lastKey The caller's last-request ref.
 * @param {Function} dispatch The mutation atom's setter — the store the caller writes.
 * @param {WellMapRequest} request The request to send.
 * @param {number} [refreshToken] Changes when the well was edited under the caller. It
 *   joins the dedup key but not the request, so an identical payload is sent again
 *   without telling the backend anything new.
 * @return {boolean} True when a request was actually sent.
 */
export const requestWellMapOnce = (
  lastKey: WellMapRequestKey,
  dispatch: (request: WellMapRequest) => void,
  request: WellMapRequest,
  refreshToken?: number
): boolean => {
  const key = JSON.stringify([request, refreshToken ?? null]);
  if (key === lastKey.current) return false;

  lastKey.current = key;
  dispatch(request);

  return true;
};

/**
 * Forgets the caller's last request so an identical one is sent again. Callers must do
 * this when their request fails, or the same well would keep the failure instead of
 * retrying for as long as the caller stays mounted.
 * @param {WellMapRequestKey} lastKey The caller's last-request ref.
 */
export const resetWellMapRequestDedup = (lastKey: WellMapRequestKey): void => {
  lastKey.current = null;
};

/** A caller's own dedup: send once per payload, forget on failure. */
export interface WellMapRequestOnce {
  send: (
    dispatch: (request: WellMapRequest) => void,
    request: WellMapRequest,
    refreshToken?: number
  ) => boolean;
  forget: () => void;
}

/**
 * The dedup for one caller, scoped to that caller's mount.
 * @return {WellMapRequestOnce} A stable `send` / `forget` pair.
 */
export const useWellMapRequestOnce = (): WellMapRequestOnce => {
  const lastKey = useRef<string | null>(null);

  return useMemo(
    () => ({
      send: (dispatch, request, refreshToken) =>
        requestWellMapOnce(lastKey, dispatch, request, refreshToken),
      forget: () => resetWellMapRequestDedup(lastKey),
    }),
    []
  );
};
